import {
  DestroyRef,
  EventEmitter,
  inject,
  Pipe,
  PipeTransform,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GroupResult } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';

/**
 * A pipe for lazy loading arrays and GroupResult structures in chunks.
 *
 * This pipe supports two main use cases:
 * 1. Regular arrays: Loads items in chunks as needed
 * 2. GroupResult arrays: Handles nested group structures with group-aware pagination
 *
 * Features:
 * - Lazy loading with configurable chunk sizes
 * - Group boundary respect for GroupResult arrays
 * - Support for deeply nested GroupResult structures (multi-level grouping)
 * - Preserves group metadata and structure during pagination
 * - Automatic detection of GroupResult vs regular array types
 * - Memory efficient - only loads what's needed
 *
 * @example
 * // Regular array usage
 * items | lazyArray:5:loadTrigger
 *
 * @example
 * // GroupResult array usage (automatically detected)
 * groupedData | lazyArray:10:loadTrigger
 *
 * @example
 * // Nested GroupResult usage (type->category->subcategory grouping)
 * nestedGroupedData | lazyArray:20:loadTrigger
 */
@Pipe({ name: 'lazyArray', })
export class LazyArrayPipe implements PipeTransform {
  destroyRef = inject(DestroyRef);

  transform<T>(
    array: ReadonlyArray<T>,
    chunkSize: number,
    loadOn: EventEmitter<unknown> | Observable<unknown>,
  ): WritableSignal<Array<T>>;

  transform(
    array: ReadonlyArray<GroupResult>,
    chunkSize: number,
    loadOn: EventEmitter<unknown> | Observable<unknown>,
  ): WritableSignal<Array<GroupResult>>;

  transform<T>(
    array: ReadonlyArray<T | GroupResult>,
    chunkSize: number,
    loadOn: EventEmitter<unknown> | Observable<unknown>,
  ): WritableSignal<Array<T | GroupResult>> {
    if (array.length > 0 && this.isGroupResultArray(array)) {
      return this.transformGroupResults(
        array as ReadonlyArray<GroupResult>,
        chunkSize,
        loadOn,
      );
    } else {
      return this.transformRegularArray(
        array as ReadonlyArray<T>,
        chunkSize,
        loadOn,
      );
    }
  }

  private isGroupResultArray(array: ReadonlyArray<unknown>): boolean {
    return (
      array.length > 0 &&
      array[0] !== null &&
      typeof array[0] === 'object' &&
      'items' in array[0] &&
      'aggregates' in array[0]
    );
  }

  private transformRegularArray<T>(
    array: ReadonlyArray<T>,
    chunkSize: number,
    loadOn: EventEmitter<unknown> | Observable<unknown>,
  ): WritableSignal<Array<T>> {
    let currentChunk = 1;
    let startIndex = 0;
    let endIndex = chunkSize;
    const initialChunk = array.slice(startIndex, endIndex);

    const lazyArray = signal<Array<T>>(initialChunk);

    loadOn.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      currentChunk++;
      startIndex = (currentChunk - 1) * chunkSize;
      endIndex = currentChunk * chunkSize;

      if (startIndex >= array.length) {
        return;
      }

      const nextChunk = array.slice(startIndex, endIndex);
      lazyArray.update((value) => [...value, ...nextChunk]);
    });

    return lazyArray;
  }

  private transformGroupResults(
    array: ReadonlyArray<GroupResult>,
    chunkSize: number,
    loadOn: EventEmitter<unknown> | Observable<unknown>,
  ): WritableSignal<Array<GroupResult>> {
    const totalItems = array.reduce(
      (total, group) => total + this.countTotalItems(group),
      0,
    );
    let totalItemsShown = 0;

    const initialGroups = this.getNestedGroupChunk(
      array,
      chunkSize,
      totalItemsShown,
    );
    const lazyArray = signal<Array<GroupResult>>(initialGroups.groups);
    totalItemsShown = initialGroups.totalItemsShown;

    loadOn.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (totalItemsShown >= totalItems) {
        return;
      }

      const nextGroups = this.getNestedGroupChunk(
        array,
        chunkSize,
        totalItemsShown,
      );
      totalItemsShown = nextGroups.totalItemsShown;

      if (nextGroups.groups.length > 0) {
        lazyArray.update((currentGroups) =>
          this.mergeNestedGroups(currentGroups, nextGroups.groups),
        );
      }
    });

    return lazyArray;
  }

  private getNestedGroupChunk(
    array: ReadonlyArray<GroupResult>,
    chunkSize: number,
    totalItemsAlreadyShown: number,
  ): { groups: Array<GroupResult>; totalItemsShown: number } {
    const result: Array<GroupResult> = [];
    let itemsRemaining = chunkSize;
    let totalItemsProcessed = 0;
    let currentGlobalItemIndex = totalItemsAlreadyShown;

    for (const group of array) {
      if (itemsRemaining <= 0) break;

      const totalItemsInThisGroup = this.countTotalItems(group);

      if (currentGlobalItemIndex >= totalItemsInThisGroup) {
        currentGlobalItemIndex -= totalItemsInThisGroup;
        continue;
      }

      const processedGroup = this.processGroupRecursively(
        group,
        itemsRemaining,
        currentGlobalItemIndex,
      );

      if (processedGroup && processedGroup.itemCount > 0) {
        result.push(processedGroup.group);
        itemsRemaining -= processedGroup.itemCount;
        totalItemsProcessed += processedGroup.itemCount;
      }

      currentGlobalItemIndex = 0;
    }

    return {
      groups: result,
      totalItemsShown: totalItemsAlreadyShown + totalItemsProcessed,
    };
  }

  private processGroupRecursively(
    group: GroupResult,
    itemsNeeded: number,
    skipItems: number,
  ): { group: GroupResult; itemCount: number } | null {
    if (this.isGroupResultArray(group.items)) {
      return this.processNestedGroups(group, itemsNeeded, skipItems);
    } else {
      return this.processLeafGroup(group, itemsNeeded, skipItems);
    }
  }

  private processNestedGroups(
    group: GroupResult,
    itemsNeeded: number,
    skipItems: number,
  ): { group: GroupResult; itemCount: number } | null {
    const nestedGroups = group.items as GroupResult[];
    const processedNestedGroups: GroupResult[] = [];
    let totalItemsProcessed = 0;
    let remainingSkipItems = skipItems;

    for (const nestedGroup of nestedGroups) {
      if (itemsNeeded - totalItemsProcessed <= 0) break;

      const totalItemsInNestedGroup = this.countTotalItems(nestedGroup);

      if (remainingSkipItems >= totalItemsInNestedGroup) {
        remainingSkipItems -= totalItemsInNestedGroup;
        continue;
      }

      const result = this.processGroupRecursively(
        nestedGroup,
        itemsNeeded - totalItemsProcessed,
        remainingSkipItems,
      );

      if (result && result.itemCount > 0) {
        processedNestedGroups.push(result.group);
        totalItemsProcessed += result.itemCount;
        remainingSkipItems = 0;
      }
    }

    if (processedNestedGroups.length > 0) {
      return {
        group: {
          ...group,
          items: processedNestedGroups,
        },
        itemCount: totalItemsProcessed,
      };
    }

    return null;
  }

  private processLeafGroup(
    group: GroupResult,
    itemsNeeded: number,
    skipItems: number,
  ): { group: GroupResult; itemCount: number } | null {
    const leafItems = group.items;
    const totalItemsInGroup = leafItems.length;

    if (skipItems >= totalItemsInGroup) {
      return null;
    }

    const startIndex = skipItems;
    const itemsToTake = Math.min(itemsNeeded, totalItemsInGroup - startIndex);
    const endIndex = startIndex + itemsToTake;

    if (itemsToTake <= 0) {
      return null;
    }

    return {
      group: {
        ...group,
        items: leafItems.slice(startIndex, endIndex),
      },
      itemCount: itemsToTake,
    };
  }

  private countTotalItems(group: GroupResult): number {
    if (this.isGroupResultArray(group.items)) {
      const nestedGroups = group.items as GroupResult[];
      return nestedGroups.reduce((total, nestedGroup) => {
        return total + this.countTotalItems(nestedGroup);
      }, 0);
    } else {
      return group.items.length;
    }
  }

  private mergeNestedGroups(
    currentGroups: Array<GroupResult>,
    nextGroups: Array<GroupResult>,
  ): Array<GroupResult> {
    const result = [...currentGroups];

    for (const nextGroup of nextGroups) {
      const existingGroupIndex = result.findIndex(
        (group) =>
          group.field === nextGroup.field && group.value === nextGroup.value,
      );

      if (existingGroupIndex !== -1) {
        const existingGroup = result[existingGroupIndex];

        if (this.isGroupResultArray(nextGroup.items)) {
          result[existingGroupIndex] = {
            ...existingGroup,
            items: this.mergeNestedGroups(
              existingGroup.items as GroupResult[],
              nextGroup.items as GroupResult[],
            ),
          };
        } else {
          result[existingGroupIndex] = {
            ...existingGroup,
            items: [...existingGroup.items, ...nextGroup.items],
          };
        }
      } else {
        result.push(nextGroup);
      }
    }

    return result;
  }
}
